import Box from '@mui/material/Box';
import CircularProgress from '@mui/material/CircularProgress';
import Typography from '@mui/material/Typography';
import type { ReactNode } from 'react';
import { deltaPercentPoints, formatCompactCurrency, formatPercent } from '../../../lib/format';
import { type BreakdownScope, type FatigueData, useFatigueTab } from '../data/api';
import { FATIGUE_SAVINGS_TIP } from '../data/constants';
import type { FatigueStatus, StatCardData } from '../data/mediaBreakdownData';
import type { MediaKpi } from '../data/types';
import { formatKpiValue } from '../data/utils';
import { CreativeDetailsLink } from '../shared/CreativeDetailsLink';
import { Delta } from '../shared/Delta';
import { ErrorState } from '../shared/ErrorState';
import { MediaCard } from '../shared/MediaCard';
import type { MediaColumn, MediaTableRow } from '../shared/MediaTable';
import { MediaTable } from '../shared/MediaTable';
import { MediaThumb } from '../shared/MediaThumb';
import { NoData } from '../shared/NoData';
import { StatCard } from '../shared/StatCard';

type FatigueCard = FatigueData['current'][number];

function fatigueColumns(kpiLabel: string): MediaColumn[] {
  return [
    { key: 'media', label: 'Media', width: 'minmax(0, 1.5fr)' },
    { key: 'spend', label: 'Spend', width: '110px' },
    { key: 'kpi', label: kpiLabel, width: '90px' },
    { key: 'decline', label: 'Decline', width: '90px' },
    { key: 'days', label: 'Days', width: '70px' },
    { key: 'triggered', label: 'Triggered', width: '140px' },
    { key: 'status', label: 'Status', width: '110px' },
  ];
}

function StatusPill({ status }: { status: FatigueStatus }): ReactNode {
  const current = status === 'current';
  return (
    <Box
      component="span"
      sx={{
        display: 'inline-flex',
        fontSize: 10,
        fontWeight: 700,
        borderRadius: 1,
        py: 0.5,
        px: 1,
        color: current ? 'warning.main' : 'success.dark',
        bgcolor: current ? 'score.medium.bg' : 'score.high.bg',
      }}
    >
      {current ? 'Fatiguing' : 'Recovered'}
    </Box>
  );
}

function fatigueRow(
  card: FatigueCard,
  status: FatigueStatus,
  kpi: MediaKpi,
  symbol: string,
  totalSpend: number,
  accountIds: readonly number[],
): MediaTableRow {
  return {
    id: card.creativeId,
    cells: {
      media: (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, minWidth: 0 }}>
          <MediaThumb src={card.thumbnailUrl} previewUrl={card.previewUrl} size={110} />
          <Box sx={{ minWidth: 0 }}>
            <Typography
              sx={{
                fontWeight: 600,
                color: 'text.primary',
                whiteSpace: 'nowrap',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
              }}
            >
              {card.creativeName}
            </Typography>
            <Typography
              sx={{
                mt: 0.5,
                fontSize: 10,
                color: 'text.secondary',
                overflowWrap: 'anywhere',
              }}
            >
              {`Creative ID: ${card.creativeId}`}
            </Typography>
            <CreativeDetailsLink creativeId={card.creativeId} accountIds={accountIds} />
          </Box>
        </Box>
      ),
      spend: (
        <Box>
          <Typography sx={{ fontWeight: 700, color: 'text.primary', fontSize: 13 }}>
            {formatCompactCurrency(card.spend, symbol)}
          </Typography>
          <Typography sx={{ fontSize: 11, color: 'text.secondary' }}>
            {`${formatPercent(totalSpend > 0 ? card.spend / totalSpend : null)} of total`}
          </Typography>
        </Box>
      ),
      kpi: (
        <Box component="span" sx={{ fontWeight: 700 }}>
          {formatKpiValue(card.kpiValue, kpi, symbol)}
        </Box>
      ),
      decline: <Delta value={deltaPercentPoints(card.decline) ?? 0} fontSize={11} />,
      days: (
        <Box component="span" sx={{ color: 'text.secondary' }}>
          {`${card.daysInFatigue}d`}
        </Box>
      ),
      triggered: (
        <Box component="span" sx={{ color: 'text.secondary' }}>
          {card.recovered ? `${card.triggered} → ${card.recovered}` : card.triggered}
        </Box>
      ),
      status: <StatusPill status={status} />,
    },
  };
}

export function MediaFatigue({
  kpi,
  scope,
  enabled,
}: {
  kpi: MediaKpi;
  scope: BreakdownScope;
  enabled: boolean;
}): ReactNode {
  const { data, isLoading, isError } = useFatigueTab(scope, enabled);

  if (isError) return <ErrorState />;

  if (isLoading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
        <CircularProgress size={22} />
      </Box>
    );
  }

  if (!data || (data.current.length === 0 && data.previous.length === 0)) {
    return <NoData />;
  }

  const symbol = data.currencySymbol;
  const cards: StatCardData[] = [
    { label: 'Currently Fatigued', value: String(data.fatiguedCount), tone: 'warning' },
    {
      label: 'Avg. Decline',
      value: data.avgDecline == null ? '-' : `${deltaPercentPoints(data.avgDecline)}%`,
    },
    {
      label: 'Avg. Days Fatiguing',
      value: data.avgDaysInFatigue == null ? '-' : data.avgDaysInFatigue.toFixed(2),
    },
    {
      label: 'Potential Savings',
      value: formatCompactCurrency(data.affectedSpend, symbol),
      tone: 'warning',
      tip: FATIGUE_SAVINGS_TIP,
      tipPlacement: 'left',
    },
  ];

  const rows: MediaTableRow[] = [
    ...data.current.map((card) =>
      fatigueRow(card, 'current', kpi, symbol, data.totalSpend, scope.accountIds ?? []),
    ),
    ...data.previous.map((card) =>
      fatigueRow(card, 'previous', kpi, symbol, data.totalSpend, scope.accountIds ?? []),
    ),
  ];

  return (
    <Box>
      <Box
        sx={{
          display: 'grid',
          gridTemplateColumns: { xs: '1fr 1fr', md: 'repeat(4, 1fr)' },
          gap: 3,
          mb: 4,
          '@media print': { gridTemplateColumns: 'repeat(4, 1fr)' },
        }}
      >
        {cards.map((card) => (
          <StatCard key={card.label} card={card} />
        ))}
      </Box>

      <MediaCard sx={{ overflow: 'hidden' }}>
        <Typography
          sx={{ fontSize: 14, fontWeight: 700, color: 'text.primary', pt: 4, pb: 2, px: 4 }}
        >
          Fatigued Media
        </Typography>
        <MediaTable columns={fatigueColumns(kpi.label)} rows={rows} />
      </MediaCard>
    </Box>
  );
}
